import * as z from "zod";
import clsx from "clsx";
import { useState, useEffect } from "react";
import { Input } from "../ui/input.tsx";
import { Label } from "../ui/label.tsx";
import { Switch } from "../ui/switch.tsx";
import { Button } from "../ui/button.tsx";
import { useApi } from "../../Api/useApi.ts";
import { Separator } from "../ui/separator.tsx";
import { useParams, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog.tsx";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu.tsx";
import {
    Settings,
    Box,
    Puzzle,
    Ellipsis,
    Loader,
    Webhook,
    BellRing,
    Sparkles,
} from "lucide-react";
import { useFormEditorContext } from "../../Contexts/FormEditorContext.jsx";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "../ui/tooltip.tsx";
import { Webhooks } from "./Integrations/Webhooks.tsx";
import { useUser } from "@clerk/clerk-react";
import { EmailNotifications } from "./Integrations/EmailNotifications.tsx";

const tabTypes = [
    {
        label: "General",
        value: "general",
        icon: <Box size={10} />,
        isPro: false,
    },
    {
        label: "Integrations",
        value: "integrations",
        icon: <Puzzle size={10} />,
        isPro: false,
    },
    {
        label: "Webhooks",
        value: "webhooks",
        icon: <Webhook size={10} />,
        isPro: false,
    },
    {
        label: "Notifications",
        value: "notifications",
        icon: <BellRing size={10} />,
        isPro: true,
    },
];

// Zod schema for form validation
const formSettingsSchema = z.object({
    name: z.string().min(1, { message: "Form name cannot be empty" }),
    pauseForm: z.boolean(),
    showProgressBar: z.boolean(),
    email_address: z
        .string()
        .min(1, { message: "Email cannot be empty" })
        .email({ message: "Invalid email address" }),
    include_submissions: z.boolean(),
    is_active: z.boolean(),
    //smart_summaries: z.boolean(),
});

interface FormSettingsUpdateParams {
    id: string;
    workspace_id: string;
    body: {
        title: string;
        settings: {
            accept_responses: boolean;
            show_progress_bar: boolean;
            email_notification: {
                is_active: boolean;
                email_address: string;
                include_submissions: boolean;
                // smart_summaries: boolean;
            };
        };
    };
}

type FormSettingsProps = {
    setIntegrationDialog(open: boolean): void;
    setIntegrationType(type: string): void;
};

const FormSettingsModal = ({
    setIntegrationDialog,
    setIntegrationType,
}: FormSettingsProps) => {
    const api = useApi();
    const { user } = useUser();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { id, workspaceId } = useParams();
    const {
        setFormSettings,
        setName,
        name,
        formSettings,
        usersPlan,
        setShowPricingModal,
    } = useFormEditorContext();

    const [tab, setTab] = useState<string>("general");
    const [openSettingDialog, setOpenSettingDialog] = useState<boolean>(false);

    // Default values from existing form settings
    const defaultValues = {
        name: name,
        pauseForm: formSettings?.accept_responses,
        showProgressBar: formSettings?.show_progress_bar,
        email_address: formSettings?.email_notification?.email_address || "",
        include_submissions:
            formSettings?.email_notification?.include_submissions,
        is_active: formSettings?.email_notification?.is_active,
        // smart_summaries: formSettings?.email_notification?.smart_summaries,
    };

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty },
        watch,
    } = useForm({
        resolver: zodResolver(formSettingsSchema),
        defaultValues,
        mode: "onChange",
        values: defaultValues,
    });

    const updateFormSettingsMutation = useMutation({
        mutationFn: (params: FormSettingsUpdateParams) =>
            api.forms.formSettingsUpdate(
                params.id,
                params.workspace_id,
                params.body,
                params.body.title,
            ),
        onSuccess: () => {
            setIsLoading(false);
            setOpenSettingDialog(false);
        },

        onError: (error) => {
            setIsLoading(false);
            setOpenSettingDialog(false);
            console.error("Error details:", error);
        },
    });

    const onSubmit = (data) => {
        setIsLoading(true);
        setName(data.name);
        setFormSettings((prev) => {
            return {
                ...prev,
                accept_responses: data.pauseForm,
                show_progress_bar: data.showProgressBar,
                email_notification: {
                    email_address: data.email_address,
                    include_submissions: data.include_submissions,
                    is_active: data.is_active,
                    //  smart_summaries: data.smart_summaries,
                },
            };
        });
        updateFormSettingsMutation.mutate({
            id: id as string,
            workspace_id: workspaceId as string,
            body: {
                title: data.name,
                settings: {
                    accept_responses: data.pauseForm,
                    show_progress_bar: data.showProgressBar,
                    email_notification: {
                        is_active: data.is_active,
                        email_address: data.email_address,
                        include_submissions: data.include_submissions,
                        // smart_summaries: data.smart_summaries,
                    },
                },
            },
        });
        setOpenSettingDialog(false);
    };

    const handleSettingsPopupOpen = (e) => {
        e.preventDefault();
        setOpenSettingDialog(true);
    };

    return (
        <Dialog
            open={openSettingDialog}
            onOpenChange={() => setOpenSettingDialog(false)}
        >
            <Button
                variant={"ghost"}
                className=" bg-[#F1F2F4] rounded-md h-9 px-2.5 py-2.5 "
                onClick={handleSettingsPopupOpen}
            >
                <Settings size={16} />
            </Button>

            <DialogContent className="sm:w-[840px] max-w-[840px] max-h-[600px] w-full p-0 gap-0 top-[50%]">
                <DialogHeader className="p-3 h-[61px] ">
                    <DialogTitle className="flex items-center gap-1">
                        <Settings size={13} />
                        <span className=" text-[14px]"> Form Settings</span>
                    </DialogTitle>
                    <DialogDescription className=" text-[12px]">
                        Customize the form's name, submissions, etc...
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col items-center h-full">
                    <div className="flex w-full border-b border-b-[#F1F2F4] h-[478px]">
                        <div className="w-[204px] flex flex-col gap-1 h-full p-3 border-r border-t border-t-[#F1F2F4] border-r-[#F1F2F4]">
                            {tabTypes?.map((tabType) => (
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <button
                                                className={clsx(
                                                    "flex w-full h-8 gap-1.5 items-center hover:cursor-pointer px-[9px] rounded-[8px]",
                                                    tab === tabType.value
                                                        ? "bg-[#F1F2F4] text-[#191B33]"
                                                        : "text-[#626F86]",
                                                )}
                                                key={tabType.value}
                                                onClick={() => {
                                                    if (
                                                        usersPlan === "free" &&
                                                        tabType.isPro
                                                    ) {
                                                        setShowPricingModal(
                                                            true,
                                                        );
                                                    } else {
                                                        setTab(tabType.value);
                                                    }
                                                }}
                                            >
                                                {tabType.icon}

                                                <span className=" text-[12px] font-normal text-primary">
                                                    {tabType.label}
                                                </span>
                                                {tabType.isPro && <ProBadge />}
                                            </button>
                                        </TooltipTrigger>
                                        {usersPlan === "free" && (
                                            <TooltipContent className="text-xs max-w-[200px]">
                                                This feature is available for
                                                Pro users only.
                                            </TooltipContent>
                                        )}
                                    </Tooltip>
                                </TooltipProvider>
                            ))}
                        </div>
                        {tab === "general" && (
                            <form className="w-full space-y-4 border-t h-full border-t-[#F1F2F4] ">
                                <div className="flex flex-col gap-4 mb-4">
                                    {/* Form Name Input */}
                                    <div className="flex-col">
                                        <div className="flex flex-col gap-1 px-3 pt-3 mb-4">
                                            <Label>Form Name</Label>
                                            <Label className="text-xs font-normal text-[#44546F]">
                                                This name is for your internal
                                                use only
                                            </Label>
                                            <Controller
                                                name="name"
                                                control={control}
                                                render={({ field }) => (
                                                    <Input
                                                        {...field}
                                                        className="mt-1 focus:border-[#A1A1AA] border-[#E4E4E7] focus-visible:ring-offset-0 h-9 focus-visible:outline-none focus-visible:ring-0"
                                                        placeholder="Form Name"
                                                        autoFocus
                                                    />
                                                )}
                                            />
                                            {errors.name && (
                                                <p className="mt-1 text-xs text-red-500">
                                                    {errors.name.message}
                                                </p>
                                            )}
                                        </div>
                                        <Separator />
                                        {/* Pause Form Switch */}
                                        <div className="flex items-center justify-between px-3 mb-4">
                                            <div className="flex flex-col gap-1 mt-[16px]">
                                                <Label>Accept Responses</Label>
                                                <Label className="text-xs font-normal text-[#44546F]">
                                                    When turned off, people will
                                                    not be able to submit your
                                                    form
                                                </Label>
                                            </div>
                                            <Controller
                                                name="pauseForm"
                                                control={control}
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <Switch
                                                        checked={value}
                                                        onCheckedChange={
                                                            onChange
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>
                                        <Separator />
                                        {/* Progress Bar Visibility Switch */}
                                        <div className="flex items-center justify-between px-3 mb-4">
                                            <div className="flex flex-col gap-1 mt-[16px]">
                                                <Label>
                                                    Progress Bar Visibility
                                                </Label>
                                                <Label className="text-xs font-normal text-[#44546F]">
                                                    Show or hide the form
                                                    progress bar
                                                </Label>
                                            </div>
                                            <Controller
                                                name="showProgressBar"
                                                control={control}
                                                render={({
                                                    field: { value, onChange },
                                                }) => (
                                                    <Switch
                                                        checked={value}
                                                        onCheckedChange={
                                                            onChange
                                                        }
                                                    />
                                                )}
                                            />
                                        </div>

                                        <Separator />
                                    </div>
                                </div>
                            </form>
                        )}

                        {tab === "integrations" && (
                            <FormIntegrations
                                setIntegrationDialog={setIntegrationDialog}
                                setIntegrationType={setIntegrationType}
                                setOpenSettingDialog={setOpenSettingDialog}
                            />
                        )}
                        {tab === "webhooks" && <Webhooks />}
                        {tab === "notifications" && (
                            <div className="w-full space-y-4 border-t h-full border-t-[#F1F2F4] ">
                                <div className="flex-col">
                                    <div className="flex flex-col gap-1 px-3 pt-3 mb-4">
                                        <Label>Email Notifications</Label>
                                        <Label className="text-xs font-normal text-[#44546F]">
                                            Get email notifications letting you
                                            know of a new entry on your
                                            Buildform. <br />
                                            We’ll send form submissions to this
                                            address
                                        </Label>
                                    </div>
                                    <Separator className="mb-0 " />
                                </div>

                                {/* Add new */}
                                <div className="flex-col mt-4 ">
                                    <div className="flex flex-col gap-1 px-3 pt-3 mb-4">
                                        <Label>Add an Email</Label>
                                        <div className="flex items-center justify-between ">
                                            <Label className="text-xs font-normal text-[#44546F]">
                                                We’ll send form submissions to
                                                this address
                                            </Label>
                                            <div className="flex items-center gap-1 ">
                                                <Label className="text-xs font-normal text-[#44546F]">
                                                    Email Notifications
                                                </Label>
                                                <Controller
                                                    name="is_active"
                                                    control={control}
                                                    render={({
                                                        field: {
                                                            value,
                                                            onChange,
                                                        },
                                                    }) => (
                                                        <Switch
                                                            checked={value}
                                                            onCheckedChange={
                                                                onChange
                                                            }
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                        <Controller
                                            name="email_address"
                                            control={control}
                                            render={({ field }) => (
                                                <Input
                                                    {...field}
                                                    className="mt-1 focus:border-[#A1A1AA] border-[#E4E4E7] focus-visible:ring-offset-0 h-9 focus-visible:outline-none focus-visible:ring-0"
                                                    placeholder="hello@example.com"
                                                    autoFocus
                                                />
                                            )}
                                        />
                                        {errors.email_address && (
                                            <p className="mt-1 text-xs text-red-500">
                                                {errors.email_address.message}
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex items-center justify-between px-4 mb-4">
                                        <div className="flex flex-col gap-1 mt-[16px]">
                                            <Label>Include Submissions</Label>
                                            <Label className="text-xs font-normal text-[#44546F]">
                                                Toggle ON to receive submission
                                                content
                                            </Label>
                                        </div>
                                        <Controller
                                            name="include_submissions"
                                            control={control}
                                            render={({
                                                field: { value, onChange },
                                            }) => (
                                                <Switch
                                                    checked={value}
                                                    onCheckedChange={onChange}
                                                />
                                            )}
                                        />
                                    </div>

                                    {/* uncomment this after completing the feature */}
                                    {/* <div className="flex items-center justify-between px-4 mb-4">
                                        <div className="flex flex-col gap-1 mt-[16px]">
                                            <div className="flex items-center gap-1 ">
                                                <Sparkles size={14} />
                                                <Label>Smart Summaries</Label>
                                            </div>
                                            <Label className="text-xs font-normal text-[#44546F]">
                                                A brief AI-generated overview
                                                instead of the full form details
                                            </Label>
                                        </div>
                                        <Controller
                                            name="smart_summaries"
                                            control={control}
                                            render={({
                                                field: { value, onChange },
                                            }) => (
                                                <Switch
                                                    checked={value}
                                                    onCheckedChange={onChange}
                                                />
                                            )}
                                        />
                                    </div> */}
                                    <Separator />
                                </div>
                            </div>
                        )}
                    </div>
                    {(tab === "general" || tab === "notifications") && (
                        <div className=" flex w-full justify-end items-center pr-2 h-[61px]">
                            <DialogFooter className="flex gap-3">
                                <DialogClose
                                    className={clsx(
                                        "text-[#F44336] text-xs font-normal hover:text-[#F44336]",
                                        !isDirty && " hidden",
                                    )}
                                >
                                    Discard
                                </DialogClose>

                                <Button
                                    className={clsx(
                                        " text-white hover:text-white hover:bg-[#191B33] bg-[#191B33] rounded-[8px] h-9 w-[102px] font-normal text-xs",
                                        !isDirty &&
                                            "bg-[#F1F2F4] text-[#626F86] hover:bg-[#E4E4E7]",
                                    )}
                                    onClick={handleSubmit(onSubmit)}
                                    disabled={!isDirty}
                                >
                                    {isLoading ? (
                                        <Loader className="animate-spin" />
                                    ) : (
                                        "Save Changes"
                                    )}
                                </Button>
                            </DialogFooter>
                        </div>
                    )}

                    <div className=" flex w-full justify-end items-center pr-2 h-[61px]"></div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default FormSettingsModal;

type FormIntegrationsProps = {
    setIntegrationDialog(open: boolean): void;
    setIntegrationType(type: string): void;
    setOpenSettingDialog(open: boolean): void;
};

const STATUS = {
    ACTIVE: "active",
    ERROR: "error",
    PAUSED: "paused",
};

const FormIntegrations = ({
    setIntegrationDialog,
    setIntegrationType,
    setOpenSettingDialog,
}: FormIntegrationsProps) => {
    const api = useApi();
    const { id, workspaceId } = useParams();

    /* Remove this when the google integration is completed from the backend  */
    const { user } = useUser();
    const [alreadyAuthorized, setAlreadyAuthorized] = useState<boolean>(false);

    /* Remove this when the google integration is completed from the backend  */
    useEffect(() => {
        const googleAccount = user?.externalAccounts.find(
            (account) => account.provider === "google",
        );

        if (googleAccount) {
            const requiredScopes = [
                "https://www.googleapis.com/auth/drive.file",
            ];

            const approvedScopes =
                googleAccount.approvedScopes?.split(" ") || [];
            const hasAllScopes = requiredScopes.every((scope) =>
                approvedScopes.includes(scope),
            );

            setAlreadyAuthorized(hasAllScopes);
        }
    }, [user]);

    const { formSettings, setFormSettings } = useFormEditorContext();
    const [isConnectedToGoogleSheet, setIsConnectedToGoogleSheet] =
        useState<boolean>(
            formSettings?.integrations?.google_sheets_integration?.status ===
                STATUS.ACTIVE
                ? true
                : false,
        );
    const [googleSheetStatus, setGoogleSheetStatus] = useState<string | null>(
        formSettings?.integrations?.google_sheets_integration?.status || null,
    );
    const [
        isGoogleSheetIntegrationAvailable,
        setIsGoogleSheetIntegrationAvailable,
    ] = useState<boolean>(
        formSettings?.integrations?.google_sheets_integration?.spreadsheet_url
            ? true
            : false,
    );

    const updateGoogleSheetIntegrationStatus = useMutation({
        mutationFn: ({
            integrationType,
            integrationStatus,
        }: {
            integrationType: string;
            integrationStatus: string;
        }) =>
            api.integrations.pauseIntegration(
                id as string,
                workspaceId as string,
                integrationType,
                integrationStatus,
            ),
        onSuccess: () => {
            const newStatus = isConnectedToGoogleSheet
                ? STATUS.ACTIVE
                : STATUS.PAUSED;
            setFormSettings((prev: any) => {
                return {
                    ...prev,
                    integrations: {
                        ...prev.integrations,
                        google_sheets_integration: {
                            ...prev.integrations.google_sheets_integration,
                            status: newStatus,
                        },
                    },
                };
            });
        },
        onError: (error) => {
            console.error("Error details:", error);
        },
    });

    const handleGoogleSheetIntegrationStatus = (
        integrationType: string,
        integrationStatus: string,
    ) => {
        const requestStatus =
            integrationStatus === STATUS.ACTIVE ? "resume" : "pause";

        const newStatus = isConnectedToGoogleSheet
            ? STATUS.PAUSED
            : STATUS.ACTIVE;

        setFormSettings((prev: any) => {
            return {
                ...prev,
                integrations: {
                    ...prev.integrations,
                    google_sheets_integration: {
                        ...prev.integrations.google_sheets_integration,
                        status: newStatus,
                    },
                },
            };
        });

        updateGoogleSheetIntegrationStatus.mutate({
            integrationType,
            integrationStatus: requestStatus,
        });
    };

    const deleteIntegrationMutation = useMutation({
        mutationFn: (integrationType: string) =>
            api.integrations.deleteIntegration(
                id as string,
                workspaceId as string,
                integrationType,
            ),
        onSuccess: (data) => {
            if (data?.status === 204) {
                setFormSettings((prev: any) => {
                    return {
                        ...prev,
                        integrations: {
                            ...prev.integrations,
                            google_sheets_integration: {
                                ...prev.integrations.google_sheets_integration,
                                status: null,
                                spreadsheet_url: null,
                            },
                        },
                    };
                });
                setIsGoogleSheetIntegrationAvailable(false);
            }
        },
        onError: (error) => {
            console.error("Error details:", error);
        },
    });
    const handleDeleteIntegration = (integrationType: string) => {
        deleteIntegrationMutation.mutate(integrationType);
    };

    const isDraftedForm = sessionStorage.getItem("formStatus") === "draft";

    return (
        <div className="w-full space-y-4 border-t h-full border-t-[#F1F2F4] ">
            <div className="flex flex-col gap-4 mb-4">
                <div className="flex-col">
                    <div className="flex flex-col gap-1 px-3 pt-3 mb-4">
                        <Label>Form Integrations</Label>
                        <Label className="text-xs font-normal text-[#44546F]">
                            Connect Buildform to your favorite apps
                        </Label>
                    </div>
                    <Separator />
                </div>
                {/* GoogleSheet */}
                <div className="flex-col">
                    <div className="flex items-center justify-between px-4">
                        <div className="flex items-center">
                            <img
                                src="/google_sheet.png"
                                alt="google_sheet"
                                className="w-[29.5px] -mt-3 "
                            />

                            <div className="flex flex-col gap-1 px-3 pt-3 mb-4">
                                <Label>Google Sheets</Label>
                                <Label className="text-xs font-normal text-[#44546F]">
                                    Send your data straight to Google Sheets.
                                    Automatically syncs as results <br /> come
                                    in.{" "}
                                    {isGoogleSheetIntegrationAvailable && (
                                        <Link
                                            to={
                                                formSettings?.integrations
                                                    ?.google_sheets_integration
                                                    ?.spreadsheet_url
                                            }
                                            target="_blank"
                                            className="underline "
                                        >
                                            {" "}
                                            View your Spreadsheet
                                        </Link>
                                    )}
                                </Label>
                            </div>
                        </div>
                        {isGoogleSheetIntegrationAvailable ? (
                            <div className="flex items-center gap-3">
                                <Switch
                                    checked={isConnectedToGoogleSheet}
                                    onCheckedChange={(checked) => {
                                        setIsConnectedToGoogleSheet(checked);
                                        handleGoogleSheetIntegrationStatus(
                                            "google_sheets_integration",
                                            checked
                                                ? STATUS.ACTIVE
                                                : STATUS.PAUSED,
                                        );
                                    }}
                                />
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            className="p-0 hover:bg-transparent"
                                        >
                                            <Ellipsis size={16} />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent
                                        align="end"
                                        className="w-[130px] flex-col hover:cursor-pointer items-center flex justify-center h-10 rounded-[8px]"
                                    >
                                        <Button
                                            variant={"ghost"}
                                            className=" font-normal  hover:cursor-pointer p-0 text-xs text-[#F44336] hover:bg-transparent hover:text-[#F44336]"
                                            onClick={() => {
                                                handleDeleteIntegration(
                                                    "google_sheets_integration",
                                                );
                                            }}
                                        >
                                            Delete Intergration
                                        </Button>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        ) : (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger>
                                        {/*  Remove this when the google integration is completed from the backend   */}
                                        {alreadyAuthorized ? (
                                            <Button
                                                className="bg-[#191B33] text-white rounded-[8px] h-9 w-[102px] font-normal text-xs"
                                                onClick={() => {
                                                    setOpenSettingDialog(false);
                                                    setIntegrationDialog(true);
                                                    setIntegrationType(
                                                        "googleSheet",
                                                    );
                                                }}
                                                disabled={isDraftedForm}
                                            >
                                                Connect
                                            </Button>
                                        ) : (
                                            <Button
                                                className="bg-[#191B33] text-white rounded-[8px] h-9 w-[102px] font-normal text-xs"
                                                disabled
                                            >
                                                Coming Soon
                                            </Button>
                                        )}
                                    </TooltipTrigger>
                                    {isDraftedForm && alreadyAuthorized && (
                                        <TooltipContent>
                                            <TooltipContent
                                                className={clsx(
                                                    "text-xs max-w-[200px]",
                                                )}
                                            >
                                                You need to publish your form
                                                before connecting to Google
                                                Sheets.
                                            </TooltipContent>
                                        </TooltipContent>
                                    )}
                                </Tooltip>
                            </TooltipProvider>
                        )}
                    </div>
                    <Separator />
                </div>
                <div className="flex-col">
                    <div className="flex items-center justify-between px-4">
                        <div className="flex items-center">
                            <img
                                src="/zaiper.png"
                                alt="zaiper"
                                className="w-[29.5px] -mt-3 "
                            />

                            <div className="flex flex-col gap-1 px-3 pt-3 mb-4">
                                <Label>Zapier</Label>
                                <Label className="text-xs font-normal text-[#44546F]">
                                    Our integration with Zapier allows you to
                                    seamlessly transfer data collected on
                                    <br /> Buildform to over 2,000 services.
                                </Label>
                            </div>
                        </div>
                        <Button
                            disabled
                            className="bg-[#191B33] text-white rounded-[8px] h-9 w-[102px] font-normal text-xs"
                        >
                            Coming Soon
                        </Button>
                    </div>
                    <Separator />
                </div>
                <div className="flex-col">
                    <div className="flex items-center justify-between px-4">
                        <div className="flex items-center">
                            <img
                                src="/hubspot.png"
                                alt="hubspot"
                                className="w-[29.5px] -mt-3 "
                            />

                            <div className="flex flex-col gap-1 px-3 pt-3 mb-4">
                                <Label>HubSpot</Label>
                                <Label className="text-xs font-normal text-[#44546F]">
                                    Send contact, company, or deal info to
                                    HubSpot to quickly follow up on <br /> new
                                    leads or update existing details to your
                                    free HubSpot account.
                                </Label>
                            </div>
                        </div>
                        <Button
                            disabled
                            className="bg-[#191B33] text-white rounded-[8px] h-9 w-[102px] font-normal text-xs"
                        >
                            Coming Soon
                        </Button>
                    </div>
                    <Separator />
                </div>
            </div>
        </div>
    );
};

function ProBadge() {
    return (
        <div className="bg-[#DBEAFE] px-[8px] py-[1px] rounded-full">
            <p className="text-[10px] font-bold text-[#2563EB]">PRO</p>
        </div>
    );
}
